/**
 * @module app.cm
 */

import { autoinject } from 'aurelia-framework';
import { bindable } from 'aurelia-typed-observable-plugin';
import { ComponentBase } from 'app/shared/lib/essette-site';
import { ITasksConfig, INotesConfig, IQueuesConfig } from 'app/core/models/essette-interfaces';
import { RouteConfig, NavigationInstruction } from 'aurelia-router';
import { ITaskBinding } from 'app/core/components/tasks/task';
import { apiCall } from 'app/shared/lib/api/api';
import { TaskService } from 'app/core/services/task-service';
import { IApiQueryResults } from 'app/shared/lib/api/api-types';
import { safe } from 'app/shared/lib/utilities';
import { CasePageContext } from 'app/cm/lib/case-page-context';
import { I18N } from 'aurelia-i18n';
import * as CM from 'app/cm/models/essette-api';


@autoinject()
export class InterventionDetailsPage extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    protected interventionId!: string;
    protected goalId: string | null = null;
    protected case: CM.Case | null = null;
    protected taskBinding: ITaskBinding | null = null;

    @bindable.string({ changeHandler: 'titleReload' })
    protected taskDisplay: string | null = null;

    protected routeConfig: RouteConfig | null = null;

    protected tasksConfig: ITasksConfig = {
        primaryTargetType: 'CM.CarePlanIntervention',
        serviceRootUrl: 'cm/careplans',
        targetType: 'CM.CarePlanGoal',
        labelKey: 'cm:carePlanIntervention.$',
        backRouteName: 'cm.case.careplan',
        addTargetProperty: this.addGoalProperty.bind(this),
        appointmentsConfig: {
            addTargetProperty: (appointment: CM.CarePlanInterventionAppointment) => { appointment.intervention = { id: this.interventionId }; }
        },
        smartTasksConfig: {
            moduleTaskCode: 'CM.Task', // for smart tasks like Refer to CM to use as Referral Origin
            assessmentsConfig: {
                serviceRootUrl: 'cm/cases',
                targetType: 'CM.Case',
                backRouteName: 'cm.case.assessments'
            },
            documentsConfig: {
                serviceRootUrl: 'cm/cases',
                targetType: 'CM.Case',
                addTargetProperty: (document: CM.CaseDocument) => { document.case = { id: safe(() => this.case!.id) }; }
            },
            notesConfig: {
                serviceRootUrl: 'cm/cases',
                targetType: 'CM.Case',
                addTargetProperty: (note: CM.CaseNote) => { note.case = { id: safe(() => this.case!.id) }; }
            },
            correspondenceConfig: {
                transitionalPageId: '3079',
                moduleCode: 'CM',
                serviceRootUrl: 'cm/cases',
                rollupRootUrl: 'cm/cases/rollup/correspondence'
            },
            contactLogConfig: {
                serviceRootUrl: 'cm/cases',
                targetType: 'CM.Case',
                rollupConfig: {
                    serviceRootUrl: 'cd/members',
                    targetType: 'CD.Member'
                }
            }
        }
    };

    private notesConfig: INotesConfig = {
        serviceRootUrl: 'cm/careplans/interventions',
        targetType: 'CM.CarePlanIntervention',
        addTargetProperty: this.addInterventionProperty.bind(this),
        excludeSystem: false,
        flagCodes: ['PP.CaseSummary'],
        hideFlagOption: true
    };

    private queuesConfig: IQueuesConfig = {
        serviceRootUrl: 'cm/tasks',
        targetType: 'CM.Intervention'
    };


    ////****************** Element References ***************************************////



    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly service: TaskService,
        private readonly context: CasePageContext,
        private readonly i18n: I18N,
        ...rest: any[]) {

        super(...rest);
        this.logger.source = this;
    }

    protected async activate(params: { interventionId: string }, routeConfig: RouteConfig, navInstruction: NavigationInstruction): Promise<void> {
        if(!params.interventionId) { throw new Error('Missing required parameter [interventionId]'); }

        this.interventionId = params.interventionId;
        this.tasksConfig.memberId = this.context.case?.member?.id || undefined;

        this.service.configure(this.tasksConfig);

        await this.loadIntervention(this.interventionId);
        if(!this.goalId) { throw new Error('Could not load goal id'); }
        if(!this.case || !this.case.id) { throw new Error('Case was not loaded'); }
        this.context.case = this.case;
        if(!this.case.member || !this.case.member.id) { throw new Error('Could not load member id'); }

        await this.accountContext.load();
        const canEditClosedCase = this.accountContext.hasPermission('CM.Case.Duty.EditClosedCase');

        this.taskBinding = {
            targetId: this.goalId,
            taskId: this.interventionId,
            smartTaskTargetId: this.case.id,
            tasksConfig: this.tasksConfig,
            notesConfig: this.notesConfig,
            queuesConfig: this.queuesConfig,
            readonly: this.context.caseIsReadOnly(canEditClosedCase),
            relatedModels: {
                "CD.Member": this.case.member.id,
                "CM.Case": this.case.id
            },
            canDeactivateNotes: false,
            showAppointments: false,
            showTaskQueues: false,
            canUpdateTaskDueDate: false,
            showDelegateToQueue: false,
            showDelegateToUser: false,
            canSaveTask: false,
        };

        this.routeConfig = routeConfig;
    }


    ////****************** Events ***************************************************////
    protected titleReload(): void {
        if(this.routeConfig?.navModel) {
            this.routeConfig.navModel.setTitle(this.i18n.tr('pp:routes.intervention.$title', { what: this.taskDisplay }));
        }
    }

    ////****************** Functions ************************************************////
    public addGoalProperty(task: CM.CarePlanIntervention): void {
        task.goal = { id: this.goalId };
    }

    public addInterventionProperty(model: CM.CarePlanInterventionNote | CM.CarePlanInterventionAppointment): void {
        model.intervention = { id: this.interventionId };
    }


    ////****************** Service Calls ********************************************////
    @apiCall
    private async loadIntervention(interventionId: string): Promise<void> {
        const includes: string[] = ['case'];
        try {
            this.state.beginLoad();
            const response: IApiQueryResults<CM.CarePlanIntervention> = await this.service.loadTask(interventionId, includes);
            this.case = response.results[0].case || null;
            this.goalId = safe(() => response.results[0].goal!.id) || null;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }
}
